<script setup lang='ts'>
import { useGtm } from '@gtm-support/vue-gtm'
import { toNumber } from 'lodash'

const props = defineProps<{
  id: string
  audio: string
  title: string
  theme: 'light' | 'dark'
}>()

const gtm = useGtm()
const bgImageRef: Ref<string | null> = ref(null)
const playerRef: Ref<HTMLAudioElement | null> = ref(null)
const seekerRef: Ref<HTMLInputElement | null> = ref(null)

const state = ref({
  isPlaying: false,
  duration: 0,
  currentTime: 0,
  isLoaded: false,
  percent: 0,
  id: props.id,
})

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)

  return (
    `${minutes < 10 ? `0${minutes}` : `${minutes}`
       }:${
       secs < 10 ? `0${secs}` : `${secs}`}`
  )
}

function endListener() {
  state.value.isPlaying = false
  state.value.currentTime = 0
  bgImageRef.value = `-webkit-gradient(linear, left top, right top, color-stop(
      ${0}, #f99a34), color-stop(${0}, ${props.theme === 'light' ? '#ffffff' : '#3688C320'}))`

  gtm?.trackEvent({
    event: 'audio',
    audioPlayerAction: 'finished',
    audioTitle: `${props.title}`,
  })
}

function canPlayListener() {
  state.value.isLoaded = true
}

function playbackListener() {
  state.value.duration = Math.round(playerRef.value ? playerRef.value.duration : 0)
}

function pauseListener() {
  gtm?.trackEvent({
    event: 'audio',
    audioPlayerAction: 'pause',
    audioTitle: `${props.title}`,
  })
}

function playListener() {
  gtm?.trackEvent({
    event: 'audio',
    audioPlayerAction: 'play',
    audioTitle: `${props.title}`,
  })
}

function togglePlay() {
  if (playerRef.value?.paused) {
    playerRef.value.play()
    state.value.isPlaying = true
  }
  else {
    playerRef.value?.pause()
    state.value.isPlaying = false
  }
}

onMounted(() => {
  nextTick(() => {
    if (playerRef.value) {
      playerRef.value.addEventListener('loadedmetadata', playbackListener)
      playerRef.value.addEventListener('canplay', canPlayListener)
      playerRef.value.addEventListener('ended', endListener)
      playerRef.value.addEventListener('play', playListener)
      playerRef.value.addEventListener('pause', pauseListener)
    }

    const storage = sessionStorage.getItem('player')
    const playerState = storage ? JSON.parse(storage) : null
    if (playerState?.id === state.value.id) {
      if (playerRef.value) playerRef.value.currentTime = playerState?.time || 0
      state.value.currentTime = playerState?.time || 0
      if (playerState?.isPlaying)
        togglePlay()
    }
    else {
      sessionStorage.removeItem('player')
    }
  })
})

onBeforeUnmount(() => {
  if (playerRef.value) {
    playerRef.value.removeEventListener('loadedmetadata', playbackListener)
    playerRef.value.removeEventListener('canplay', canPlayListener)
    playerRef.value.removeEventListener('ended', endListener)
    playerRef.value.removeEventListener('play', playListener)
    playerRef.value.removeEventListener('pause', pauseListener)
  }
})

function timeUpdate() {
  const percent = Math.floor((100 * (playerRef.value ? playerRef.value.currentTime : 1)) / state.value.duration)
  sessionStorage.setItem(
    'player',
    JSON.stringify({
      time: state.value.currentTime,
      isPlaying: state.value.isPlaying,
      id: state.value.id,
    }),
  )
  if (state.value.percent + 10 <= percent) {
    state.value.percent = percent
    gtm?.trackEvent({
      event: 'audio',
      audioPlayerAction: `Progress ${percent}%`,
      audioTitle: `${props.title}`,
    })
  }

  const diff = Math.abs(state.value.currentTime - (playerRef.value ? playerRef.value.currentTime : 0))
  if (diff > 0.1) {
    state.value.currentTime = playerRef.value ? playerRef.value.currentTime : 0
    const seeker = seekerRef.value
    const value = toNumber(seeker?.value || 0)
    const min = toNumber(seeker?.getAttribute('min'))
    const max = toNumber(seeker?.getAttribute('max'))

    const val = (value - min) / (max - min)
    bgImageRef.value = `-webkit-gradient(linear, left top, right top, color-stop(
        ${val}, #f99a34), color-stop(${val}, ${props.theme === 'light' ? '#ffffff' : '#3688C320'}))`
  }
}

function seekTime(el: Event) {
  if (playerRef.value)
    playerRef.value.currentTime = Math.round(toNumber((el.target as HTMLInputElement).value || 0))

  const element = seekerRef.value
  const value = toNumber(element?.value || 0)
  const min = toNumber(element?.getAttribute('min'))
  const max = toNumber(element?.getAttribute('max'))

  const val = (value - min) / (max - min)
  bgImageRef.value = `-webkit-gradient(linear, left top, right top, color-stop(
      ${val}, #f99a34), color-stop(${val}, ${props.theme === 'light' ? '#ffffff' : '#3688C320'}))`
}
</script>

<template>
  <ClientOnly>
    <audio
      ref="playerRef"
      class="hidden"
      controls
      :ontimeupdate="timeUpdate"
      preload="metadata"
    >
      <source :src="audio" type="audio/mp3">
      Your browser does not support the audio tag.
    </audio>
    <div class="player hidden md:flex">
      <div class="player__controls">
        <div
          class="player__play-button"
          @click="togglePlay"
        >
          <AppIcon
            v-if="state.isPlaying"
            class="text-white player__pause-icon"
            :icon="['fas', 'pause']"
          />
          <AppIcon
            v-else
            class="text-white player__play-icon"
            :icon="['fas', 'play']"
          />
        </div>
      </div>
      <div :class="`player__time player__time--${theme}`">
        {{ formatTime(state.currentTime) }}
      </div>
      <div :class="`player__progressbar player__progressbar--${theme}`">
        <input
          ref="seekerRef"
          v-model="state.currentTime"
          type="range"
          min="0"
          :max="state.duration"
          name="position"
          class="w-full h-full"
          :style="{ backgroundImage: `${bgImageRef}` }"
          @change="seekTime"
        >
      </div>
      <div :class="`player__time player__time--${theme}`">
        {{ formatTime(state.duration) }}
      </div>
    </div>
    <div class="player flex flex-col md:hidden">
      <div :class="`player__progressbar player__progressbar--${theme}`">
        <input
          ref="seekerRef"
          v-model="state.currentTime"
          type="range"
          min="0"
          :max="state.duration"
          name="position"
          class="w-full h-full"
          :style="{ backgroundImage: `${bgImageRef}` }"
          @change="seekTime"
        >
      </div>
      <div class="flex justify-center mt-3">
        <div :class="`player__time player__time--${theme}`">
          {{ formatTime(state.currentTime) }}
        </div>
        <div class="player__controls">
          <div
            class="player__play-button"
            @click="togglePlay"
          >
            <AppIcon
              v-if="state.isPlaying"
              class="text-white player__pause-icon"
              :icon="['fas', 'pause']"
            />
            <AppIcon
              v-else
              class="text-white player__play-icon"
              :icon="['fas', 'play']"
            />
          </div>
        </div>
        <div :class="`player__time player__time--${theme}`">
          {{ formatTime(state.duration) }}
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style lang='scss' scoped>
.player {
  @apply mt-4;

  &__time {
    @apply m-auto mx-4;

    &--light {
      @apply text-white;
    }

    &--dark {
      @apply text-black;
    }
  }

  &__play-button {
    @apply rounded-full bg-warning m-auto cursor-pointer w-10 h-10 flex items-center justify-center;
  }

  &__pause-icon {
    @apply text-center w-full;
  }

  &__play-icon {
    margin-left: 3px;
    @apply text-center w-full;
  }

  &__progressbar {
    @apply w-full flex items-center;

    input[type='range'] {
      @apply rounded cursor-pointer;
      -webkit-appearance: none;
      height: 10px;
    }

    input[type='range']::-webkit-slider-thumb {
      -webkit-appearance: none;
      border: none;
      height: 0;
      width: 0;
    }

    input[type='range']::-moz-range-thumb {
      -webkit-appearance: none;
      border: none;
      height: 0;
      width: 0;
    }
  }

  &__progressbar--light {
    input[type='range'] {
      background-image: -webkit-gradient(linear, left top, right top, color-stop(0, #ffffff));
    }
  }

  &__progressbar--dark {
    input[type='range'] {
      background-image: -webkit-gradient(linear, left top, right top, color-stop(0, #3688C320));
    }
  }
}
</style>
